/**
 * Step 3's questions — before the plans run, and again under "Things that change the answer" (cashflow
 * rebuild, 21 Oct 2026; the village's RvQuestions, owner rulings 20–21 Oct 2026).
 *
 * WHAT WE ALREADY KNOW first, from the file, in plain words: what could be moved (cash, term deposits and
 * shares; never the home or a super pension), that nothing here moves house, and that the emergency money is
 * kept back. Then only three questions, each with its "what's this?":
 *   1. how much stays in the bank for emergencies — kept back when money moves, drawn on only if a year runs
 *      short (owner, 21 Oct 2026: never "no plan will touch it");
 *   2. a guaranteed income for life — the shared question (GuaranteedIncomeQuestions), the client's own answer,
 *      "we work out how much";
 *   3. which of the other levers they would consider — funeral bonds, a gift within the free area, moving
 *      savings where the tax is lower — each explained in one line.
 * The years are one line with "why 10?". "Build a plan yourself instead" sits beside the run button.
 */
import React, { useState } from 'react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { householdVoice } from '../../utils/householdWords.ts';
import GuaranteedIncomeQuestions, { type GuaranteedIncomeGoals } from '../../components/strategy/GuaranteedIncomeQuestions.tsx';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { cashflowReserveOf, cashflowLeversOf, EMERGENCY_RESERVE, type CashflowLever } from './cashflowPensionCandidates.ts';
import { totalLiquid, payableDebtsForCashflow } from './cashflowPensionScenario.ts';
import { CP_LEVER_WORDS } from './cpPlanWords.ts';
import { money, link, whyLink, WHY_TEN_YEARS } from './cpUi.tsx';
import { QuestionTitle, BuildYourselfLine } from '../../components/strategy/QuestionParts.tsx';

export interface CpAnswers {
    reserve: number;
    levers: CashflowLever[];
    years: number;
}

export interface CpQuestionsProps {
    factFind: DraftFactFind;
    /** The guaranteed-income writer (App's, for those goals keys alone). Absent: the question is not asked. */
    onGuaranteedIncomeGoals?: (patch: GuaranteedIncomeGoals) => void;
    legacyYears: number;
    firstRun: boolean;
    onRun: (answers: CpAnswers) => void;
    onBuildYourOwn: () => void;
    onCancel?: () => void;
}

const Why = ({ open, onToggle, children }: { open: boolean; onToggle: () => void; children: React.ReactNode }) => (
    <>
        {' '}<button type="button" onClick={onToggle} aria-expanded={open} className={`text-[12px] ${whyLink}`}>what&apos;s this?</button>
        {open && <span className="mt-1.5 block rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-white px-3 py-2 text-[13px] font-normal leading-relaxed text-slate-700">{children}</span>}
    </>
);

const Q = ({ n, title, why, whyOpen, onWhy, children, testId }: { n: number; title: React.ReactNode; why?: React.ReactNode; whyOpen?: boolean; onWhy?: () => void; children: React.ReactNode; testId?: string }) => (
    <div className="border-t border-slate-200 pt-4" data-testid={testId}>
        <QuestionTitle n={n}>{title}{why && onWhy && <Why open={!!whyOpen} onToggle={onWhy}>{why}</Why>}</QuestionTitle>
        <div className="mt-2">{children}</div>
    </div>
);

const LEVER_OPTIONS: Array<{ id: CashflowLever; key: 'bond' | 'gift' | 'super' }> = [
    { id: 'bond', key: 'bond' },
    { id: 'gift', key: 'gift' },
    { id: 'tax', key: 'super' },
];

const CpQuestions = ({ factFind, onGuaranteedIncomeGoals, legacyYears, firstRun, onRun, onBuildYourOwn, onCancel }: CpQuestionsProps) => {
    const v = householdVoice(factFind);
    const [reserve, setReserve] = useState(() => cashflowReserveOf(factFind));
    const [levers, setLevers] = useState<CashflowLever[]>(() => cashflowLeversOf(factFind));
    const [years, setYears] = useState(legacyYears);
    const [openWhy, setOpenWhy] = useState<string | null>(null);
    const whyProps = (id: string) => ({ whyOpen: openWhy === id, onWhy: () => setOpenWhy((o) => (o === id ? null : id)) });
    const liquid = totalLiquid(factFind);
    const movable = Math.max(0, liquid - reserve);
    const debts = payableDebtsForCashflow(factFind).reduce((t, d) => t + d.balance, 0);
    const toggle = (id: CashflowLever) => setLevers((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
    let qn = 0;
    const next = () => { qn += 1; return qn; };

    return (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="cp-questions">
            <h2 className="text-[22px] font-bold text-brand-navy">{onGuaranteedIncomeGoals ? 'Three' : 'Two'} choices that shape the plans</h2>
            <p className="mt-1 text-[15px] text-slate-600">Everything else comes from {v.possessive} file. Our suggestions are filled in, and each can be changed later.</p>
            <BuildYourselfLine onClick={onBuildYourOwn} testId="cp-build-own-top" />

            <div className="mt-4 rounded-xl border border-slate-200 border-l-4 border-l-brand-navy bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="cp-what-we-know">
                <strong className="text-slate-900">What we already know</strong>
                <ul className="mt-1.5 list-disc space-y-1 pl-5">
                    <li><strong className="text-slate-900">{v.Names} {v.have} {money(liquid)} that could be moved</strong> — cash, term deposits and shares. The home and any super pension stay as they are.</li>
                    <li><strong className="text-slate-900">After the {money(reserve)} kept for emergencies, {money(movable)} is free to rearrange.</strong> Every plan is sized from that, never from more.</li>
                    <li><strong className="text-slate-900">Nobody moves house.</strong> Downsizing, a retirement village or a granny flat each has its own section, because the trade-offs are different.</li>
                    {debts > 0 && <li>{v.Names} owe{v.s} {money(debts)}. Paying it off is a choice {v.they} can make when building a plan {v.you ? 'yourself' : 'by hand'}.</li>}
                </ul>
            </div>

            <div className="mt-4 space-y-4">
                <Q n={next()} title="How much should stay in the bank for emergencies?" {...whyProps('reserve')} why={<>Money kept in the bank for the unexpected. We keep it back when we move {v.their} money, and draw on it only if a year runs short. Centrelink still counts it.</>} testId="cp-q-reserve">
                    <div className="flex flex-wrap items-center gap-3">
                        <CurrencyInput value={reserve} onChange={(x) => setReserve(Math.max(0, x ?? 0))} className="w-36" ariaLabel="Money kept for emergencies" testId="cp-reserve" />
                        <span className="text-[13px] text-slate-500">
                            We suggest {money(EMERGENCY_RESERVE)}.
                            {Math.round(reserve) !== EMERGENCY_RESERVE && <> <button type="button" onClick={() => setReserve(EMERGENCY_RESERVE)} className={link}>Use {money(EMERGENCY_RESERVE)}</button></>}
                        </span>
                    </div>
                    <p className="mt-1.5 text-[13.5px] text-slate-700">We keep it back when we move {v.their} money, and draw on it only if a year runs short.</p>
                </Q>

                {onGuaranteedIncomeGoals && (
                    <Q n={next()} title="A guaranteed income for life" testId="cp-guaranteed-income">
                        <GuaranteedIncomeQuestions
                            factFind={factFind}
                            onGoals={onGuaranteedIncomeGoals}
                            investable={movable}
                            investableLead={`After the ${money(reserve)} kept for emergencies`}
                            investableMadeOf="cash, term deposits and shares"
                            inCare={guaranteedShapeAvailable(factFind as never, 'client')}
                            idPrefix="cp"
                        />
                        <p className="mt-2 text-[13.5px] leading-relaxed text-slate-700"><strong className="text-slate-900">We work out how much.</strong> We test amounts up to that share. Where it would lift the Age Pension, we also test the amount that lifts it most, held to the same share.</p>
                    </Q>
                )}

                <Q n={next()} title={`Which of these would ${v.names} consider?`} testId="cp-q-levers">
                    <p className="mb-1 text-[13.5px] text-slate-600">Tick what {v.they}&apos;d consider; the plans try each one, and the two together.</p>
                    {LEVER_OPTIONS.map((o) => {
                        const w = CP_LEVER_WORDS[o.key];
                        return (
                            <label key={o.id} className="flex cursor-pointer items-start gap-2 py-1.5 text-[14px] text-slate-700">
                                <input type="checkbox" checked={levers.includes(o.id)} onChange={() => toggle(o.id)} className="mt-1 h-4 w-4 accent-blue-600" data-testid={`cp-lever-${o.id}`} />
                                <span>
                                    <strong className="text-slate-900">{o.id === 'tax' ? 'Moving savings where the tax is lower' : w.name}</strong> — {o.id === 'tax' ? `${CP_LEVER_WORDS.super.what} Where super can’t take it, ${CP_LEVER_WORDS.investmentBond.name.toLowerCase()} instead.` : w.what} {w.centrelink}
                                </span>
                            </label>
                        );
                    })}
                    <p className="mt-1.5 text-[13.5px] text-slate-700">Anything {v.they} don&apos;t move stays in {v.their} <strong className="text-slate-900">bank account</strong>, and changing nothing is always tested too, so {v.they} can see what each choice adds.</p>
                </Q>
            </div>

            <p className="mt-5 text-[13px] text-slate-500">
                We plan for{' '}
                <input type="number" min={1} max={30} value={years} onChange={(e) => setYears(Math.min(30, Math.max(1, parseInt(e.target.value, 10) || 1)))} className="w-14 rounded border border-slate-300 px-1.5 py-0.5 text-center text-[13px] font-semibold text-slate-900" aria-label="Years to plan for" /> years.
                <Why open={openWhy === 'years'} onToggle={() => setOpenWhy((o) => (o === 'years' ? null : 'years'))}>{WHY_TEN_YEARS}</Why>
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <span className="text-[14px] text-slate-700">Prefer to set the numbers yourself? <button type="button" onClick={onBuildYourOwn} className={link} data-testid="cp-build-own">Build a plan yourself instead</button></span>
                <span className="flex items-center gap-3">
                    {!firstRun && onCancel && <button type="button" onClick={onCancel} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800">Cancel</button>}
                    <button type="button" onClick={() => onRun({ reserve, levers, years })} className="rounded-lg bg-blue-600 px-5 py-2.5 text-[15px] font-semibold text-white hover:bg-blue-700" data-testid="cp-run">{firstRun ? 'Test the ways to improve it' : 'Test them again'}</button>
                </span>
            </div>
        </div>
    );
};

export default CpQuestions;
